import { useMemo } from "react";
import { FamilyHistory, PatientFamilyHistoryControllerApi } from "@/services";
import { asPaginatedContent, getConfigObject } from "@/utils/utils";
import { usePastFamilyHistoryCreate } from "@/queries/history/past-family/use-patient-family-history-create";
import { usePastFamilyHistoryUpdate } from "@/queries/history/past-family/use-patient-family-history-update";
import { useArchivePatientFamilyHistory } from "@/queries/history/past-family/use-patient-family-history-archive";
import { HistoryRow } from "../readmission-f2f-types";
import { mapFamilyHistoryToRow } from "../readmission-f2f-utils";
import {
  FAMILY_HISTORY_RECORD_NAME,
  HISTORY_LIST_PAGE_SIZE,
  PATIENT_FAMILY_HISTORY_LIST_KEY,
} from "../readmission-f2f-constant";
import {
  HistorySectionAdapter,
  UseHistorySection,
  useHistorySection,
} from "./use-history-section";

const buildPayload = (
  row: HistoryRow,
  patientId?: string,
  appointmentId?: string
): FamilyHistory => ({
  uuid: row.uuid,
  conditionName: row.name.trim(),
  note: row.notes.trim() || undefined,
  active: true,
  patient: { [patientId ?? ""]: "" },
  appointmentId: appointmentId ?? undefined,
});

export const useFamilyHistorySection = (): UseHistorySection => {
  const { mutateAsync: create, isPending: isCreating } =
    usePastFamilyHistoryCreate();
  const { mutateAsync: update, isPending: isUpdating } =
    usePastFamilyHistoryUpdate();
  const { mutateAsync: archive, isPending: isArchiving } =
    useArchivePatientFamilyHistory();

  const adapter: HistorySectionAdapter = useMemo(
    () => ({
      listKey: PATIENT_FAMILY_HISTORY_LIST_KEY,
      recordName: FAMILY_HISTORY_RECORD_NAME,
      isSaving: isCreating || isUpdating || isArchiving,
      fetchRows: ({ patientId, appointmentId, xTENANTID }) =>
        new PatientFamilyHistoryControllerApi(getConfigObject())
          .getPatientFamilyHistory(
            patientId,
            0,
            HISTORY_LIST_PAGE_SIZE,
            undefined,
            undefined,
            true,
            appointmentId ?? undefined,
            undefined,
            false,
            xTENANTID
          )
          .then((res) =>
            (
              asPaginatedContent<FamilyHistory>(res?.data)?.data?.content ?? []
            ).map(mapFamilyHistoryToRow)
          ),
      createEntry: ({ row, patientId, appointmentId, xTENANTID }) =>
        create({
          familyHistory: buildPayload(row, patientId, appointmentId),
          xTENANTID,
        }),
      updateEntry: ({ row, patientId, appointmentId, xTENANTID }) =>
        update({
          familyHistory: buildPayload(row, patientId, appointmentId),
          xTENANTID,
        }),
      archiveEntry: ({ uuid, xTENANTID }) =>
        archive({ patientFamilyHistoryId: uuid, status: true, xTENANTID }),
    }),
    [create, update, archive, isCreating, isUpdating, isArchiving]
  );

  return useHistorySection(adapter);
};
